import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';
import api from './api';
import {
  Subscription,
  SubscriptionCreate,
  SubscriptionUpdate,
  SubscriptionFilters,
  SubscriptionTransition,
  SubscriptionTransitionRequest,
  SubscriptionType,
  SubscriptionTypeCreate,
  SubscriptionTypeUpdate,
  UserSubscription,
  CompanySubscription,
  AssignUserRequest,
  OrderData,
  OnboardingData,
  TrialCreateOptions,
  TrialLimitations,
  UsageEvent,
  FeatureCheckRequest,
  FeatureCheckResult,
  SubscriptionStateHistory,
  SubscriptionStartResponse,
} from '../../types/subscription';

/**
 * Service for managing subscriptions and subscription-related operations
 * @feature PLATFORM_API_ACCESS - Provides API access for subscription management
 */
export class SubscriptionService extends BaseService {
  /**
   * Get all subscriptions with optional filtering
   * Note: Backend doesn't have a simple list endpoint, so this aggregates from user/company endpoints
   * For now, returns empty array. Use getUserSubscriptions or getCompanySubscriptions instead.
   */
  static async getAll(filters?: SubscriptionFilters): Promise<Subscription[]> {
    // Backend doesn't have a simple GET /api/v1/subscriptions endpoint
    // This would need to aggregate from user/company subscription endpoints
    // For now, return empty array and log a warning
    console.warn('[SubscriptionService] getAll() called but backend has no simple list endpoint. Use getUserSubscriptions() or getCompanySubscriptions() instead.');
    return [];
  }

  /**
   * Get subscription by ID
   * ISSUE-110 fix: Now requires context (userId or companyId) to call correct endpoint
   */
  static async getById(
    id: string,
    context?: { userId?: string; companyId?: string }
  ): Promise<Subscription | null> {
    if (!id || id === 'null' || id === 'undefined') {
      console.warn('[SubscriptionService] getById called with invalid ID:', id);
      return null;
    }

    try {
      // If context is provided, use scoped endpoints
      if (context?.companyId) {
        return await this.getCompanySubscriptionById(context.companyId, id);
      } else if (context?.userId) {
        return await this.getUserSubscriptionById(context.userId, id);
      } else {
        // No context provided - log warning and return null
        // This prevents calling the non-existent generic endpoint
        console.warn(
          '[SubscriptionService] getById called without context. Provide either userId or companyId.',
          { subscriptionId: id }
        );
        return null;
      }
    } catch (error) {
      console.error('[SubscriptionService] Error fetching subscription:', error);
      return null;
    }
  }

  /**
   * Create a new subscription
   */
  static async create(data: SubscriptionCreate): Promise<Subscription> {
    return this.post<Subscription>(
      API_ENDPOINTS.SUBSCRIPTIONS.BASE,
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Update subscription
   */
  static async update(id: string, data: SubscriptionUpdate): Promise<Subscription> {
    return this.put<Subscription>(
      API_ENDPOINTS.SUBSCRIPTIONS.BY_ID(id),
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Delete/Cancel subscription
   */
  static async delete(id: string): Promise<void> {
    try {
      await api.delete(API_ENDPOINTS.SUBSCRIPTIONS.BY_ID(id));
    } catch (error: any) {
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin');
      if (!isCorsError) {
        console.error(`DELETE request failed for ${API_ENDPOINTS.SUBSCRIPTIONS.BY_ID(id)}:`, error);
      }
      throw error;
    }
  }

  /**
   * Execute state machine transition (Backend: /subscriptions/state-machine/transition/{id}).
   */
  static async transitionState(
    id: string,
    request: SubscriptionTransitionRequest,
    isUserSubscription: boolean = false
  ): Promise<Subscription> {
    return this.transitionSubscriptionState(id, request.target_state, isUserSubscription);
  }


  /**
   * Get user's subscriptions
   */
  static async getUserSubscriptions(userId: string): Promise<UserSubscription[]> {
    try {
      return await this.get<UserSubscription[]>(
        API_ENDPOINTS.SUBSCRIPTIONS.USER_SUBSCRIPTIONS(userId)
      );
    } catch (error) {
      console.error('[SubscriptionService] Error fetching user subscriptions:', error);
      return [];
    }
  }

  /**
   * Get a specific user subscription by ID
   */
  static async getUserSubscriptionById(userId: string, subscriptionId: string): Promise<Subscription | null> {
    try {
      return await this.get<Subscription>(
        API_ENDPOINTS.SUBSCRIPTIONS.USER_SUBSCRIPTION_BY_ID(userId, subscriptionId)
      );
    } catch (error) {
      console.error('[SubscriptionService] Error fetching user subscription:', error);
      return null;
    }
  }

  /**
   * Get company's subscriptions
   */
  static async getCompanySubscriptions(companyId: string): Promise<CompanySubscription[]> {
    try {
      return await this.get<CompanySubscription[]>(
        API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_SUBSCRIPTIONS(companyId)
      );
    } catch (error) {
      console.error('[SubscriptionService] Error fetching company subscriptions:', error);
      return [];
    }
  }

  /**
   * Get a specific company subscription by ID
   */
  static async getCompanySubscriptionById(companyId: string, subscriptionId: string): Promise<Subscription | null> {
    try {
      return await this.get<Subscription>(
        API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_SUBSCRIPTION_BY_ID(companyId, subscriptionId)
      );
    } catch (error) {
      console.error('[SubscriptionService] Error fetching company subscription:', error);
      return null;
    }
  }

  /**
   * Create a company subscription
   */
  static async createCompanySubscription(data: {
    company_id: string;
    subscription_type_id: string;
    period_start_date?: string;
    period_end_date?: string;
    is_active?: boolean;
    metadata?: Record<string, any>;
  }): Promise<CompanySubscription> {
    return this.post<CompanySubscription>(
      API_ENDPOINTS.SUBSCRIPTIONS.CREATE_COMPANY_SUBSCRIPTION,
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Create a first-class user subscription (ownership layer B).
   * POST /subscriptions/users
   */
  static async createUserSubscription(data: {
    user_id: string;
    subscription_type_id: string;
    period_start_date: string;
    period_end_date?: string;
    auto_renew?: boolean;
  }): Promise<Record<string, unknown>> {
    return this.post<Record<string, unknown>>(
      API_ENDPOINTS.SUBSCRIPTIONS.CREATE_USER_SUBSCRIPTION,
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * List seat assignments on a company subscription (layer C).
   * GET /subscriptions/{companySubscriptionId}/users
   */
  static async getCompanySubscriptionSeats(
    companySubscriptionId: string
  ): Promise<UserSubscription[]> {
    try {
      return await this.get<UserSubscription[]>(
        API_ENDPOINTS.SUBSCRIPTIONS.SUBSCRIPTION_USERS(companySubscriptionId)
      );
    } catch (error) {
      console.error('[SubscriptionService] Error fetching subscription seats:', error);
      return [];
    }
  }

  /**
   * Assign subscription to user
   */
  static async assignUser(
    subscriptionId: string,
    request: AssignUserRequest
  ): Promise<UserSubscription> {
    return this.post<UserSubscription>(
      API_ENDPOINTS.SUBSCRIPTIONS.ASSIGN_USER(subscriptionId),
      request as unknown as Record<string, unknown>
    );
  }

  /**
   * Remove user assignment from subscription
   */
  static async unassignUser(subscriptionId: string, userId: string): Promise<void> {
    try {
      await api.delete(API_ENDPOINTS.SUBSCRIPTIONS.UNASSIGN_USER(subscriptionId, userId));
    } catch (error: any) {
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin');
      if (!isCorsError) {
        console.error(`DELETE request failed for unassign user:`, error);
      }
      throw error;
    }
  }

  /**
   * Delete a company subscription ownership row.
   * Prefer companySubscriptionId (company_subscriptions.id); falls back to subscriptionId.
   */
  static async unassignCompany(
    subscriptionId: string,
    companyId: string,
    companySubscriptionId?: string
  ): Promise<void> {
    const ids = [companySubscriptionId, subscriptionId].filter(
      (id, i, arr): id is string => Boolean(id) && arr.indexOf(id) === i
    );
    let lastError: unknown;
    for (const id of ids) {
      try {
        await api.delete(API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_SUBSCRIPTION_BY_ID(companyId, id));
        return;
      } catch (err: unknown) {
        lastError = err;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status !== 404) throw err;
      }
    }
    throw lastError ?? new Error('Failed to delete company subscription');
  }

  /**
   * Update a user-owned subscription (layer B).
   * PUT /subscriptions/users/{userId}/subscriptions/{subscriptionId}
   */
  static async updateUserSubscription(
    userId: string,
    subscriptionId: string,
    data: Record<string, unknown>
  ): Promise<Subscription> {
    return this.put<Subscription>(
      API_ENDPOINTS.SUBSCRIPTIONS.USER_SUBSCRIPTION_BY_ID(userId, subscriptionId),
      data
    );
  }

  /**
   * Transform backend subscription type response to frontend SubscriptionType interface
   * Maps subscription_code -> code, subscription_name -> name
   */
  private static transformSubscriptionType(data: any): SubscriptionType {
    return {
      id: data.id,
      code: data.code || data.subscription_code || '',
      name: data.name || data.subscription_name || '',
      description: data.description,
      billing_period: data.billing_period || 'monthly',
      base_price: data.base_price !== undefined && data.base_price !== null ? (typeof data.base_price === 'string' ? parseFloat(data.base_price) : Number(data.base_price)) : undefined,
      currency: data.currency || 'USD',
      is_active: data.is_active !== undefined ? data.is_active : true,
      features: data.features || [],
      prime_artifact_id: data.prime_artifact_id,
      metadata: data.metadata,
      created_at: data.created_at,
      updated_at: data.updated_at,
    };
  }

  /**
   * Transform array of backend subscription type responses
   */
  private static transformSubscriptionTypes(data: any[]): SubscriptionType[] {
    return data.map(item => this.transformSubscriptionType(item));
  }

  /**
   * Get subscription types
   */
  static async getSubscriptionTypes(): Promise<SubscriptionType[]> {
    try {
      const response = await this.get<any[]>(API_ENDPOINTS.SUBSCRIPTION_TYPES.BASE);
      return this.transformSubscriptionTypes(response);
    } catch (error) {
      console.error('[SubscriptionService] Error fetching subscription types:', error);
      return [];
    }
  }

  /**
   * Get subscription type by code
   */
  static async getSubscriptionTypeByCode(code: string): Promise<SubscriptionType | null> {
    try {
      const response = await this.get<any>(API_ENDPOINTS.SUBSCRIPTION_TYPES.BY_CODE(code));
      return response ? this.transformSubscriptionType(response) : null;
    } catch (error) {
      console.error('[SubscriptionService] Error fetching subscription type:', error);
      return null;
    }
  }

  /**
   * Get subscription type by ID
   */
  static async getSubscriptionTypeById(id: string): Promise<SubscriptionType | null> {
    try {
      const response = await this.get<any>(API_ENDPOINTS.SUBSCRIPTION_TYPES.BY_ID(id));
      return response ? this.transformSubscriptionType(response) : null;
    } catch (error) {
      console.error('[SubscriptionService] Error fetching subscription type:', error);
      return null;
    }
  }

  /**
   * Create a new subscription type
   */
  static async createSubscriptionType(data: SubscriptionTypeCreate | any): Promise<SubscriptionType> {
    const response = await this.post<any>(
      API_ENDPOINTS.SUBSCRIPTION_TYPES.CREATE,
      data as unknown as Record<string, unknown>
    );
    return this.transformSubscriptionType(response);
  }

  /**
   * Update subscription type
   */
  static async updateSubscriptionType(id: string, data: SubscriptionTypeUpdate | any): Promise<SubscriptionType> {
    const response = await this.put<any>(
      API_ENDPOINTS.SUBSCRIPTION_TYPES.UPDATE(id),
      data as unknown as Record<string, unknown>
    );
    return this.transformSubscriptionType(response);
  }

  /**
   * Delete subscription type
   */
  static async deleteSubscriptionType(id: string): Promise<void> {
    try {
      await api.delete(API_ENDPOINTS.SUBSCRIPTION_TYPES.DELETE(id));
    } catch (error: any) {
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin');
      if (!isCorsError) {
        console.error(`DELETE request failed for ${API_ENDPOINTS.SUBSCRIPTION_TYPES.DELETE(id)}:`, error);
      }
      throw error;
    }
  }


  /**
   * Start Krysalis Business Core subscription
   * ISSUE-003: Returns subscription start response with hierarchy information
   */
  static async startBusinessCoreSubscription(data: {
    user_id: string;
    company_id?: string;
    subscription_type_code?: string;
    initial_data?: Record<string, any>;
  }): Promise<SubscriptionStartResponse> {
    return this.post<SubscriptionStartResponse>(
      API_ENDPOINTS.SUBSCRIPTIONS.START_BUSINESS_CORE,
      data as unknown as Record<string, unknown>
    );
  }

  // ==================== Lifecycle Management ====================

  /**
   * Place an order for a subscription
   */
  static async placeOrder(orderData: OrderData): Promise<Subscription> {
    return this.post<Subscription>(
      API_ENDPOINTS.SUBSCRIPTIONS.LIFECYCLE.PLACE_ORDER,
      orderData as unknown as Record<string, unknown>
    );
  }

  /**
   * Provision a subscription
   */
  static async provisionSubscription(
    subscriptionId: string,
    isUserSubscription: boolean = false
  ): Promise<Subscription> {
    const endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.LIFECYCLE.PROVISION(subscriptionId)}?is_user_subscription=${isUserSubscription}`;
    return this.post<Subscription>(endpoint, {} as Record<string, unknown>);
  }

  /**
   * Activate a subscription
   */
  static async activateSubscription(
    subscriptionId: string,
    isUserSubscription: boolean = false
  ): Promise<Subscription> {
    const endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.LIFECYCLE.ACTIVATE(subscriptionId)}?is_user_subscription=${isUserSubscription}`;
    return this.post<Subscription>(endpoint, {} as Record<string, unknown>);
  }

  /**
   * Onboard a subscription
   */
  static async onboardSubscription(
    subscriptionId: string,
    onboardingData: OnboardingData,
    isUserSubscription: boolean = false
  ): Promise<Subscription> {
    const endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.LIFECYCLE.ONBOARD(subscriptionId)}?is_user_subscription=${isUserSubscription}`;
    return this.post<Subscription>(endpoint, onboardingData as unknown as Record<string, unknown>);
  }

  /**
   * Complete an order (with optional auto-provision, auto-activate, auto-onboard)
   */
  static async completeOrder(
    orderData: OrderData,
    options?: {
      auto_provision?: boolean;
      auto_activate?: boolean;
      auto_onboard?: boolean;
    }
  ): Promise<Subscription> {
    const params = new URLSearchParams();
    if (options?.auto_provision) params.append('auto_provision', 'true');
    if (options?.auto_activate) params.append('auto_activate', 'true');
    if (options?.auto_onboard) params.append('auto_onboard', 'true');

    const endpoint = params.toString()
      ? `${API_ENDPOINTS.SUBSCRIPTIONS.LIFECYCLE.COMPLETE}?${params.toString()}`
      : API_ENDPOINTS.SUBSCRIPTIONS.LIFECYCLE.COMPLETE;

    return this.post<Subscription>(endpoint, orderData as unknown as Record<string, unknown>);
  }

  // ==================== Trial Management ====================

  /**
   * Create a trial subscription
   */
  static async createTrial(options: TrialCreateOptions): Promise<Subscription> {
    const params = new URLSearchParams();
    if (options.user_id) params.append('user_id', options.user_id);
    if (options.company_id) params.append('company_id', options.company_id);
    if (options.subscription_type_code) params.append('subscription_type_code', options.subscription_type_code);
    if (options.trial_duration_days) params.append('trial_days', options.trial_duration_days.toString());

    const endpoint = params.toString()
      ? `${API_ENDPOINTS.SUBSCRIPTIONS.TRIALS.CREATE}?${params.toString()}`
      : API_ENDPOINTS.SUBSCRIPTIONS.TRIALS.CREATE;

    const body: Record<string, unknown> = {};
    if (options.initial_data) body.initial_data = options.initial_data;
    if (options.metadata) body.metadata = options.metadata;

    return this.post<Subscription>(endpoint, body);
  }

  /**
   * Convert a trial subscription to a paid subscription
   */
  static async convertTrial(
    trialSubscriptionId: string,
    paidSubscriptionTypeCode: string,
    isUserSubscription: boolean = false
  ): Promise<Subscription> {
    const endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.TRIALS.CONVERT(trialSubscriptionId)}?paid_subscription_type_code=${paidSubscriptionTypeCode}&is_user_subscription=${isUserSubscription}`;
    return this.post<Subscription>(endpoint, {} as Record<string, unknown>);
  }

  /**
   * Get trial limitations for a subscription
   */
  static async getTrialLimitations(
    subscriptionId: string,
    isUserSubscription: boolean = false
  ): Promise<TrialLimitations | null> {
    try {
      const endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.TRIALS.LIMITATIONS(subscriptionId)}?is_user_subscription=${isUserSubscription}`;
      return await this.get<TrialLimitations>(endpoint);
    } catch (error) {
      console.error('[SubscriptionService] Error fetching trial limitations:', error);
      return null;
    }
  }

  // ==================== Expiration and Renewal ====================

  /**
   * Get subscriptions expiring soon
   */
  static async getExpiringSoon(daysAhead: number = 30): Promise<Subscription[]> {
    try {
      const endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.EXPIRATION.EXPIRING_SOON}?days_ahead=${daysAhead}`;
      const response = await this.get<Subscription[] | { subscriptions: Subscription[] }>(endpoint);
      
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'subscriptions' in response) {
        return (response as { subscriptions: Subscription[] }).subscriptions;
      }
      return [];
    } catch (error) {
      console.error('[SubscriptionService] Error fetching expiring subscriptions:', error);
      return [];
    }
  }

  /**
   * Process subscription expirations
   */
  static async processExpiration(): Promise<{ processed: number; expired: number }> {
    try {
      return await this.post<{ processed: number; expired: number }>(
        API_ENDPOINTS.SUBSCRIPTIONS.EXPIRATION.PROCESS,
        {} as Record<string, unknown>
      );
    } catch (error) {
      console.error('[SubscriptionService] Error processing expirations:', error);
      return { processed: 0, expired: 0 };
    }
  }

  /**
   * Process subscription renewals
   */
  static async processRenewal(daysAhead: number = 30): Promise<{ processed: number; renewed: number }> {
    try {
      const endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.RENEWAL.PROCESS}?days_ahead=${daysAhead}`;
      return await this.post<{ processed: number; renewed: number }>(
        endpoint,
        {} as Record<string, unknown>
      );
    } catch (error) {
      console.error('[SubscriptionService] Error processing renewals:', error);
      return { processed: 0, renewed: 0 };
    }
  }

  /**
   * Renew a user subscription
   */
  static async renewUserSubscription(subscriptionId: string): Promise<Subscription> {
    return this.post<Subscription>(
      API_ENDPOINTS.SUBSCRIPTIONS.RENEWAL.RENEW_USER(subscriptionId),
      {} as Record<string, unknown>
    );
  }

  /**
   * Renew a company subscription
   */
  static async renewCompanySubscription(subscriptionId: string): Promise<Subscription> {
    return this.post<Subscription>(
      API_ENDPOINTS.SUBSCRIPTIONS.RENEWAL.RENEW_COMPANY(subscriptionId),
      {} as Record<string, unknown>
    );
  }

  // ==================== Feature Checking ====================

  /**
   * Check if a user has access to a feature
   */
  static async checkUserFeature(userId: string, featureCode: string): Promise<FeatureCheckResult> {
    try {
      return await this.get<FeatureCheckResult>(
        API_ENDPOINTS.SUBSCRIPTIONS.FEATURE_CHECK.USER(userId, featureCode)
      );
    } catch (error) {
      console.error('[SubscriptionService] Error checking user feature:', error);
      return {
        feature_code: featureCode,
        has_feature: false,
        source: null,
      };
    }
  }

  /**
   * Check if a company has access to a feature
   */
  static async checkCompanyFeature(companyId: string, featureCode: string): Promise<FeatureCheckResult> {
    try {
      return await this.get<FeatureCheckResult>(
        API_ENDPOINTS.SUBSCRIPTIONS.FEATURE_CHECK.COMPANY(companyId, featureCode)
      );
    } catch (error) {
      console.error('[SubscriptionService] Error checking company feature:', error);
      return {
        feature_code: featureCode,
        has_feature: false,
        source: null,
      };
    }
  }

  /**
   * Check feature access (generic endpoint)
   */
  static async checkFeature(request: FeatureCheckRequest): Promise<FeatureCheckResult> {
    try {
      return await this.post<FeatureCheckResult>(
        API_ENDPOINTS.SUBSCRIPTIONS.FEATURE_CHECK.CHECK,
        request as unknown as Record<string, unknown>
      );
    } catch (error) {
      console.error('[SubscriptionService] Error checking feature:', error);
      return {
        feature_code: request.feature_code,
        has_feature: false,
        source: null,
      };
    }
  }

  // ==================== State Machine ====================

  /**
   * Transition subscription state
   */
  static async transitionSubscriptionState(
    subscriptionId: string,
    targetState: string,
    isUserSubscription: boolean = false
  ): Promise<Subscription> {
    const endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.STATE_MACHINE.TRANSITION(subscriptionId)}?target_state=${targetState}&is_user_subscription=${isUserSubscription}`;
    return this.post<Subscription>(endpoint, {} as Record<string, unknown>);
  }

  /**
   * Get valid transitions for a subscription
   * Supports both old endpoint (without isUserSubscription) and new lifecycle endpoint
   */
  static async getValidTransitions(
    subscriptionId: string,
    isUserSubscription?: boolean
  ): Promise<SubscriptionTransition[]> {
    try {
      // Use lifecycle endpoint if isUserSubscription is provided, otherwise use old endpoint for backward compatibility
      let endpoint: string;
      if (isUserSubscription !== undefined) {
        endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.STATE_MACHINE.VALID_TRANSITIONS(subscriptionId)}?is_user_subscription=${isUserSubscription}`;
      } else {
        endpoint = API_ENDPOINTS.SUBSCRIPTIONS.VALID_TRANSITIONS(subscriptionId);
      }
      return await this.get<SubscriptionTransition[]>(endpoint);
    } catch (error) {
      console.error('[SubscriptionService] Error fetching valid transitions:', error);
      return [];
    }
  }

  // ==================== Usage and Status ====================

  /**
   * Get subscription artifact item
   * Supports both old endpoint (without isUserSubscription) and new lifecycle endpoint
   */
  static async getSubscriptionArtifactItem(
    subscriptionId: string,
    isUserSubscription?: boolean
  ): Promise<any> {
    try {
      // Use lifecycle endpoint if isUserSubscription is provided, otherwise use old endpoint for backward compatibility
      let endpoint: string;
      if (isUserSubscription !== undefined) {
        endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.USAGE.ARTIFACT_ITEM(subscriptionId)}?is_user_subscription=${isUserSubscription}`;
      } else {
        endpoint = API_ENDPOINTS.SUBSCRIPTIONS.ARTIFACT_ITEM(subscriptionId);
      }
      return await this.get<any>(endpoint);
    } catch (error) {
      console.error('[SubscriptionService] Error fetching subscription artifact item:', error);
      return null;
    }
  }

  /**
   * Get subscription state history
   */
  static async getSubscriptionStateHistory(
    subscriptionId: string,
    isUserSubscription: boolean = false
  ): Promise<SubscriptionStateHistory[]> {
    try {
      const endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.USAGE.STATE_HISTORY(subscriptionId)}?is_user_subscription=${isUserSubscription}`;
      const response = await this.get<SubscriptionStateHistory[] | { history: SubscriptionStateHistory[] }>(endpoint);
      
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'history' in response) {
        return (response as { history: SubscriptionStateHistory[] }).history;
      }
      return [];
    } catch (error) {
      console.error('[SubscriptionService] Error fetching subscription state history:', error);
      return [];
    }
  }

  /**
   * Sync subscription status
   */
  static async syncSubscriptionStatus(
    subscriptionId: string,
    isUserSubscription: boolean = false
  ): Promise<Subscription> {
    const endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.USAGE.SYNC_STATUS(subscriptionId)}?is_user_subscription=${isUserSubscription}`;
    return this.post<Subscription>(endpoint, {} as Record<string, unknown>);
  }

  /**
   * Record a usage event
   */
  static async recordUsageEvent(
    subscriptionId: string,
    eventType: string,
    metrics?: Record<string, number>,
    isUserSubscription: boolean = false
  ): Promise<void> {
    const params = new URLSearchParams();
    params.append('event_type', eventType);
    params.append('is_user_subscription', isUserSubscription.toString());

    const endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.USAGE.USAGE_EVENTS(subscriptionId)}?${params.toString()}`;
    const body: UsageEvent = {
      event_type: eventType,
      metrics,
      timestamp: new Date().toISOString(),
    };

    await this.post<void>(endpoint, body as unknown as Record<string, unknown>);
  }

  /**
   * Transition subscription to "in use" state
   */
  static async transitionToInUse(
    subscriptionId: string,
    isUserSubscription: boolean = false
  ): Promise<Subscription> {
    const endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.USAGE.TRANSITION_TO_IN_USE(subscriptionId)}?is_user_subscription=${isUserSubscription}`;
    return this.post<Subscription>(endpoint, {} as Record<string, unknown>);
  }

  /**
   * Check if subscription is in use
   */
  static async isSubscriptionInUse(
    subscriptionId: string,
    isUserSubscription: boolean = false
  ): Promise<boolean> {
    try {
      const endpoint = `${API_ENDPOINTS.SUBSCRIPTIONS.USAGE.IS_IN_USE(subscriptionId)}?is_user_subscription=${isUserSubscription}`;
      const response = await this.get<{ is_in_use: boolean } | boolean>(endpoint);
      
      if (typeof response === 'boolean') {
        return response;
      }
      if (response && typeof response === 'object' && 'is_in_use' in response) {
        return (response as { is_in_use: boolean }).is_in_use;
      }
      return false;
    } catch (error) {
      console.error('[SubscriptionService] Error checking if subscription is in use:', error);
      return false;
    }
  }
}

